import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	HostBinding,
	Inject,
	OnDestroy,
	OnInit,
} from '@angular/core';
import {
	AppSettingsService,
	CustomWindow,
	DatalayerService,
	FeatureService,
	opacityRevolve,
	PORTAL_DATA,
	WINDOW,
} from '@woolworthsnz/styleguide';
import { BannerResponse, ContextResponse } from '@woolworthsnz/trader-api';
import { BehaviorSubject, interval, Subject, takeUntil } from 'rxjs';
import { BannerItemComponent } from '../banner-item/banner-item.component';
import { NgIf, NgFor } from '@angular/common';
import { TealiumUtagService } from '@woolworthsnz/analytics';

@Component({
	animations: [...opacityRevolve],
	selector: 'dc-banner, [dcBanner]',
	template: `
		<! -- lack of whitespace is critical for :empty pseudo selector until level-4 -->
		<! -- prettier-ignore -->
		<ng-container *ngIf="_banners"
			><!--
		--><ng-container *ngFor="let banner of _banners; let i = index"
				><!--
			-->
				<div
					dcBannerItem
					[bannerItem]="banner"
					[index]="i"
					*ngIf="i === currentIndex"
					[@opacity]
					[class]="
						banner.additionalClassNames && this.isCartologyGAMSessionGroup
							? banner.additionalClassNames
							: ''
					"
				></div>
				<!--
   --></ng-container
			><!--
--></ng-container>
	`,
	styleUrls: ['./banner.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: true,
	imports: [NgIf, NgFor, BannerItemComponent],
})
export class BannerComponent implements OnInit, OnDestroy {
	@HostBinding('attr.data-bannerposition') bannerPosition: 'header' | 'shelf';

	// eslint-disable-next-line @typescript-eslint/naming-convention
	_banners: BannerResponse[];
	bannerTimeout = 7000;
	currentIndex$ = new BehaviorSubject(0);
	public isCartologyGAMSessionGroup = false;
	private _isCartologyGA4EventTrackingEnabled = false;
	private destroyed$: Subject<boolean> = new Subject();
	private promoEvent: 'promo-element-ready-for-default-impression-observability';
	private elementName: 'dc-banner';

	get currentIndex(): number {
		return this.currentIndex$.getValue();
	}

	set currentIndex(value: number) {
		this.currentIndex$.next(value);
	}

	constructor(
		@Inject(WINDOW) public window: CustomWindow,
		@Inject(PORTAL_DATA) public bannerRes: { banners: BannerResponse[]; position: 'header' | 'shelf' },
		private cdr: ChangeDetectorRef,
		private datalayerService: DatalayerService,
		private tealiumUtagService: TealiumUtagService,
		private appSettingsService: AppSettingsService,
		private featureService: FeatureService
	) {}

	ngOnInit(): void {
		this._banners = this.bannerRes.banners;
		this.bannerPosition = this.bannerRes.position;
		if (this._banners && this._banners.length > 1) {
			this.preloadImages();

			interval(this.bannerTimeout).pipe(takeUntil(this.destroyed$)).subscribe(this.incrementBannerIndex);
		}
		this._isCartologyGA4EventTrackingEnabled = this.featureService.isFeatureEnabled(
			ContextResponse.EnabledFeaturesEnum.EnableCartologyGA4EventTracking
		);
		this.isCartologyGAMSessionGroup = this.featureService.isFeatureEnabled(
			ContextResponse.EnabledFeaturesEnum.CartologyGAMSessionGroup
		);

		const banner = this._banners[this.currentIndex];
		if (this.isCartologyGAMSessionGroup && banner?.additionalClassNames?.includes('cartology-gam-ad')) {
			// skip impression tracking for cartology-gam-ad
			this.window.addEventListener(
				'promo-element-ready-for-default-impression-observability',
				this.defaultImpressionEventHandler
			);
		} else {
			this.trackBannerImpression();
		}

		if (this._banners?.length && this._banners[this.currentIndex]) {
			if (this.filterViewImpressions(this._banners[this.currentIndex])) {
				this.trackBannerImpression();
			}
		}
	}

	ngOnDestroy(): void {
		this.destroyed$.next(true);
		this.destroyed$.complete();
		if (this.isCartologyGAMSessionGroup && this.window) {
			this.window.removeEventListener(
				'promo-element-ready-for-default-impression-observability',
				this.defaultImpressionEventHandler
			);
		}
	}

	getTrackingData = (banner: any): any => ({
		id: banner.image,
		name: banner.altText,
		position: (this.currentIndex + 1).toString(),
		nameInSiteManagement: banner.name,
		altText: banner.altText,
	});

	trackBannerImpression = (): void => {
		if (!this._banners?.length) {
			return;
		}
		const banner = this._banners[this.currentIndex];
		const trackingData = this.getTrackingData(banner);

		this.datalayerService.trackBannerImpression(trackingData);
		if (this._isCartologyGA4EventTrackingEnabled) {
			this.pushPromoBannerImpression(trackingData);
		}
	};

	pushPromoBannerImpression(data: any): void {
		const currentUrl = this.appSettingsService.getSetting('currentUrl');
		const listType = this.datalayerService.getListTypeByUrl(currentUrl);
		this.tealiumUtagService.link({
			event: 'view_promotion',
			tealium_event: 'view_promotion',
			ecommerce: {
				creative_name: `${listType} Banner`,
				creative_slot: data.position,
				promotion_id: data.altText,
				promotion_name: data.nameInSiteManagement,
			},
		});
	}

	incrementBannerIndex = (): void => {
		const max = this._banners.length - 1;

		if (this.currentIndex < max) {
			this.currentIndex = this.currentIndex + 1;
		} else if (this.currentIndex === max) {
			this.currentIndex = 0;
		}

		this.cdr.markForCheck();
		this.trackBannerImpression();
	};

	preloadImages(): void {
		const images = new Array();

		this._banners.forEach((b: BannerResponse, i) => {
			images[i] = new Image();
			images[i].src = b.image;
		});
	}

	private readonly defaultImpressionEventHandler = (event: Event): void => {
		const customEvent = event as CustomEvent;
		const eventElement = customEvent.detail?.element;
		if (!eventElement || eventElement?.id === 'cartology-search-plp-banner') {
			this.trackBannerImpression();
		}
	};

	private filterViewImpressions(banner: BannerResponse): boolean {
		return (
			!this.isCartologyGAMSessionGroup ||
			(this.isCartologyGAMSessionGroup && !banner?.additionalClassNames?.includes('cartology-gam-ad'))
		);
	}
}
